import { DevItemComment } from './models';
import * as R from 'ramda';
import { ParticipantEmailTag } from './constants';
import { TextDirection } from '../../../localization/textDirection';
import { firstLast, firstLastReverse } from '../../survey/raters/module';


type CommentsByReadStatus = {
  readComments: DevItemComment[],
  unreadComments: DevItemComment[],
};


/**
 * Use this to return an object that groups the provided comments array into two sub-arrays:
 *
 * * The `readComments` property contains all of the comments in the array marked as read
 * * The `unreadComments` property contains all of the comments in the array marked as unread
 *
 * @param comments - the collection of comments to be grouped by read status
 */
export const commentsByReadStatus = (
  comments: DevItemComment[],
): CommentsByReadStatus => {
  // { readComments: [<all read comments>], unreadComments: [<all unread comments>] }
  const { readComments, unreadComments } = R.groupBy(
    (c: DevItemComment) => c.commentRead || c.email === '||PARTICIPANT||' ? 'readComments' : 'unreadComments',
    comments ?? [],
  );

  return { readComments: readComments ?? [], unreadComments: unreadComments ?? [] };
};


/**
 * Returns the total number of comments marked unread in the provided comment array
 *
 * @param comments - the comments amongst which we wish to count the total unread
 */
export const unreadCommentCount = (
  comments: DevItemComment[],
): number => comments.filter(c => !c.commentRead && c.email !== '||PARTICIPANT||').length;


/**
 * Returns true if the comment was written by the current user, otherwise false
 *
 * @param comment - the comment to check for authorship
 * @param email - the email address for which we wish to check the comment
 */
export const isCurrentUserComment = (comment: DevItemComment, email: string) => email === comment.email;


/**
 * Returns true if the comment was written by the project participant, otherwise false
 *
 * @param comment - the comment to check for authorship
 */
export const isParticipantComment = (comment: DevItemComment) => comment.email === ParticipantEmailTag;


/**
 * Returns the subtitle for dev plan collaborator pages (the collaborator's name and email), formatted for left-to-right
 * page layouts.
 *
 * @param participant - the object from whose properties we'll build the subtitle
 */
export const devItemSubtitle = (
  participant: {
    participantFirstName: string | undefined;
    participantLastName: string | undefined;
    participantEmail: string | undefined;
  } | undefined,
): string | undefined => {
  const firstName = participant?.participantFirstName;
  const lastName  = participant?.participantLastName;
  const email     = participant?.participantEmail;
  const nameStr   = firstLast({ firstName, lastName });

  if (!nameStr) {
    return email;
  }

  if (!email) {
    return email;
  }

  return `${nameStr}, ${email}`;
};


/**
 * Returns the subtitle for dev plan collaborator pages (the collaborator's name and email), formatted for right-to-left
 * page layouts.
 *
 * @param participant - the object from whose properties we'll build the subtitle
 */
export const devItemSubtitleReverse = (
  participant: {
    participantFirstName: string | undefined;
    participantLastName: string | undefined;
    participantEmail: string | undefined;
  } | undefined,
): string | undefined => {
  const firstName = participant?.participantFirstName;
  const lastName  = participant?.participantLastName;
  const email     = participant?.participantEmail;
  const nameStr   = firstLastReverse({ firstName, lastName });

  if (!nameStr) {
    return email;
  }

  if (!email) {
    return email;
  }

  return `${email} ,${nameStr}`;
};


/**
 * Returns the subtitle for dev plan collaborator pages (the collaborator's name and email), formatted according to the
 * layout direction.
 *
 * @param dir - the text direction for which the subtitle should be rendered
 * @param participant - the object from whose properties we'll build the subtitle
 */
export const devItemSubtitleLocal = (
  dir: TextDirection,
  participant: {
    participantFirstName: string | undefined;
    participantLastName: string | undefined;
    participantEmail: string | undefined;
  } | undefined,
): string | undefined => dir === TextDirection.LTR ? devItemSubtitle(participant) : devItemSubtitleReverse(participant);
